/**
 * The vocabulary of a cook's `cookTypeList` — what services they work, and on which shift.
 *
 * The old separate "MG Tag" field was merged into this list, so the shift is now named inside
 * each value. Any combination is allowed; admins manage these directly.
 */
export enum CookServiceTypeEnum {
    // Chefit shifts — FULL_DAY_CHEFIT was previously CHEFIT_CHEF.
    FULL_DAY_CHEFIT = "FULL_DAY_CHEFIT",
    FIRST_HALF_CHEFIT = "FIRST_HALF_CHEFIT",
    SECOND_HALF_CHEFIT = "SECOND_HALF_CHEFIT",
    // Party shifts — FULL_DAY_PARTY was previously PARTY_CHEF.
    FULL_DAY_PARTY = "FULL_DAY_PARTY",
    FIRST_HALF_PARTY = "FIRST_HALF_PARTY",
    SECOND_HALF_PARTY = "SECOND_HALF_PARTY",
    // Shift-only: implies no service.
    BACK_UP = "BACK_UP",
    // Services with no shift dimension.
    SUBSCRIPTION_CHEF = "SUBSCRIPTION_CHEF",
    HOME_CHEF = "HOME_CHEF",
    ROYAL_CHEF = "ROYAL_CHEF",
    PART_TIME = "PART_TIME",
}

export const CookServiceTypeLabels: Record<CookServiceTypeEnum, string> = {
    [CookServiceTypeEnum.FULL_DAY_CHEFIT]: "Full Day Chefit Chef",
    [CookServiceTypeEnum.FIRST_HALF_CHEFIT]: "First Half Chefit Chef",
    [CookServiceTypeEnum.SECOND_HALF_CHEFIT]: "Second Half Chefit Chef",
    [CookServiceTypeEnum.FULL_DAY_PARTY]: "Full Day Party Chef",
    [CookServiceTypeEnum.FIRST_HALF_PARTY]: "First Half Party Chef",
    [CookServiceTypeEnum.SECOND_HALF_PARTY]: "Second Half Party Chef",
    [CookServiceTypeEnum.BACK_UP]: "Back Up",
    [CookServiceTypeEnum.SUBSCRIPTION_CHEF]: "Subscription Chef",
    [CookServiceTypeEnum.HOME_CHEF]: "Home Chef",
    [CookServiceTypeEnum.ROYAL_CHEF]: "Royal Chef",
    [CookServiceTypeEnum.PART_TIME]: "Part-time",
};

/**
 * Any of these means the cook works chefit; likewise party. Mirrors CHEFIT_SERVICE_TYPES /
 * PARTY_SERVICE_TYPES on the backend, which is what its eligibility queries match on — so a
 * half-day cook counts exactly like a full-day one.
 */
export const CHEFIT_SERVICE_TYPES: CookServiceTypeEnum[] = [
    CookServiceTypeEnum.FULL_DAY_CHEFIT,
    CookServiceTypeEnum.FIRST_HALF_CHEFIT,
    CookServiceTypeEnum.SECOND_HALF_CHEFIT,
];

export const PARTY_SERVICE_TYPES: CookServiceTypeEnum[] = [
    CookServiceTypeEnum.FULL_DAY_PARTY,
    CookServiceTypeEnum.FIRST_HALF_PARTY,
    CookServiceTypeEnum.SECOND_HALF_PARTY,
];

/**
 * Values retired by the mgCookTag merge, mapped to their replacement. Mirrors
 * LEGACY_SERVICE_TYPE_RENAMES on the backend.
 */
export const LEGACY_SERVICE_TYPE_RENAMES: Record<string, CookServiceTypeEnum> = {
    CHEFIT_CHEF: CookServiceTypeEnum.FULL_DAY_CHEFIT,
    PARTY_CHEF: CookServiceTypeEnum.FULL_DAY_PARTY,
};

/**
 * The stored list as current-vocabulary values, for pre-selecting the edit control.
 *
 * A profile can still hold retired names — every chefit and party row does until the backfill
 * migration runs. Without translating them the edit control would open with nothing ticked and
 * a save would silently strip the cook's services; with it, the same capability is ticked and
 * saving normalises the row.
 *
 * Anything unrecognised is dropped: the save endpoint only accepts current values.
 */
export const toCurrentServiceTypes = (cookTypeList: unknown): CookServiceTypeEnum[] => {
    if (!Array.isArray(cookTypeList)) return [];
    const current = cookTypeList
        .map((value) => LEGACY_SERVICE_TYPE_RENAMES[value as string] ?? value)
        .filter((value): value is CookServiceTypeEnum =>
            Object.values(CookServiceTypeEnum).includes(value as CookServiceTypeEnum));
    return Array.from(new Set(current));
};

/** Human label for a stored value, falling back to the raw value for anything unrecognised. */
export const cookServiceTypeLabel = (value: string): string =>
    CookServiceTypeLabels[value as CookServiceTypeEnum] ?? value;

/** True when the cook works party at any time of day — party-allocation eligibility. */
export const servesParty = (cookTypeList: unknown): boolean =>
    Array.isArray(cookTypeList) && cookTypeList.some((value) => PARTY_SERVICE_TYPES.includes(value));

/** True when the cook works chefit at any time of day. */
export const servesChefit = (cookTypeList: unknown): boolean =>
    Array.isArray(cookTypeList) && cookTypeList.some((value) => CHEFIT_SERVICE_TYPES.includes(value));
